import type { ReactElement, ReactNode } from 'react';
import { IconButton } from './IconButton';

export type BannerTone = 'danger' | 'warn' | 'success' | 'info';

export interface BannerProps {
  tone: BannerTone;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
  /** When given, a "Dismiss" button is shown and calls this. */
  onDismiss?: () => void;
  /** Defaults: assertive for danger, polite otherwise. */
  live?: 'assertive' | 'polite' | 'off';
}

const TONE_CLASSES: Record<BannerTone, string> = {
  danger: 'bg-danger-bg text-danger border-danger-line',
  warn: 'bg-warn-bg text-warn border-warn-line',
  success: 'bg-success-bg text-success border-success-line',
  info: 'bg-info-bg text-info border-info-line'
};

export function Banner({ tone, title, children, actions, onDismiss, live }: BannerProps): ReactElement {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={live ?? (tone === 'danger' ? 'assertive' : 'polite')}
      className={`m-0 p-3 flex items-start gap-3 border border-solid rounded-panel font-ui text-md ${TONE_CLASSES[tone]}`}
    >
      <div className="m-0 p-0 flex flex-col gap-1 grow">
        <p className="m-0 p-0 font-semibold">{title}</p>
        {children ? <div className="m-0 p-0">{children}</div> : null}
      </div>
      {actions ? <div className="m-0 p-0 flex items-center gap-2">{actions}</div> : null}
      {onDismiss ? (
        <IconButton label="Dismiss" onPress={onDismiss}>
          <span aria-hidden="true">×</span>
        </IconButton>
      ) : null}
    </div>
  );
}
